/**
 * Pathways v3, the pure arithmetic and addressing of the learner and public surfaces
 * (docs/pathways-module/pathways-api-contract.md §5.2–§5.4, §9).
 *
 * NOTHING HERE EVALUATES A STEP. A step's state, the current step and the percent are the
 * server's (`modules/pathways/progress.py`, contract §3). This module only reads what the
 * server said, builds the addresses between the pages and the request bodies, so two
 * surfaces can never link or count differently.
 *
 * Pure: no React, no transport.
 */
// Relative: a pure module, resolvable under Jest without a shim.
import { dateLocale } from "../i18n/dateLocale";

const enc = encodeURIComponent;

// ── addresses (unprefixed; the caller applies tenantHref) ─────────────────────

/** `/parcours`: the public cards. */
export const CATALOGUE_PATH = "/parcours";

/** `/parcours/:pathwayId`: the card detail for a visitor, the pathway home for a learner. */
export const pathwayPath = (pathwayId) => `${CATALOGUE_PATH}/${enc(pathwayId)}`;

/** `/parcours/:pathwayId/etape/:stepId`: one step's screen. */
export const stepPath = (pathwayId, stepId) => `${pathwayPath(pathwayId)}/etape/${enc(stepId)}`;

// ── what a card says ──────────────────────────────────────────────────────────

/**
 * Whether a path type is shown as ONE flat list, with no level header: successive and
 * alternative hold exactly one level, whose title is not shown to learners (contract §2).
 *
 * @param {"successive"|"alternative"|"level"} pathType
 */
export const isFlatPath = (pathType) => pathType !== "level";

/**
 * The estimated length as the card states it (contract §5.2): "~N hours total", rounded to
 * the half hour, from one hour up; "~N min" under an hour; nothing at 0.
 *
 *   45  -> { unit: "minutes", value: 45 }
 *   100 -> { unit: "hours", value: 1.5 }      (1 h 40 rounds to the nearest half hour)
 *   370 -> { unit: "hours", value: 6 }
 *
 * @param {number} minutes  `estimated_minutes`, an integer >= 0
 * @returns {{ unit: "hours"|"minutes", value: number }|null}
 */
export function estimatedLength(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 60) return { unit: "minutes", value: Math.round(n) };
  return { unit: "hours", value: Math.round(n / 30) / 2 };
}

/**
 * Places left, as the card states them: nothing when the pathway is unlimited
 * (`spots_left` null), "full" at 0, otherwise the count.
 *
 * @param {number|null} spotsLeft
 * @returns {{ kind: "unlimited" } | { kind: "full" } | { kind: "left", n: number }}
 */
export function spotsState(spotsLeft) {
  if (spotsLeft === null || spotsLeft === undefined) return { kind: "unlimited" };
  if (spotsLeft <= 0) return { kind: "full" };
  return { kind: "left", n: spotsLeft };
}

/** Whether a card can still be joined: it has no capacity, or places left. */
export const isJoinable = (card) => spotsState(card.spots_left).kind !== "full";

/** Whether a pathway is sold (`price_cents > 0`) rather than free. */
export const isPaid = (card) => Number(card.price_cents) > 0;

// ── the join flow's request bodies (contract §5.2, §5.3) ──────────────────────

/**
 * The free enrolment body. The email is the session's, never sent.
 *
 * @param {string} name   as typed
 * @param {string} locale the language the visitor reads in
 * @returns {{ name: string, locale: string }}
 */
export const enrollBody = (name, locale) => ({ name: String(name).trim(), locale });

/**
 * The paid checkout body. `ref` (the affiliate attribution) is present only when there is
 * one: an absent key is "no referral", while "" would be a value the server has to read.
 *
 * @param {{ pathwayId: string, name: string, locale: string, originUrl: string,
 *           returnPath: string, ref?: string }} input
 */
export function checkoutBody({ pathwayId, name, locale, originUrl, returnPath, ref = "" }) {
  return {
    pathway_id: pathwayId,
    name: String(name).trim(),
    locale,
    origin_url: originUrl,
    return_path: returnPath,
    ...(ref ? { ref } : {}),
  };
}

/**
 * What the pathway page's query string says about a checkout that just came back
 * (contract §5.3): Stripe returns to `return_path?success=1&session_id=…`, or
 * `?cancel=1` when the buyer abandoned it.
 *
 * @param {string} search  `window.location.search`
 * @returns {{ kind: "none" } | { kind: "cancelled" } | { kind: "paid_return", sessionId: string }}
 */
export function checkoutReturn(search) {
  const q = new URLSearchParams(search || "");
  if (q.get("success") === "1" && q.get("session_id")) {
    return { kind: "paid_return", sessionId: q.get("session_id") };
  }
  if (q.get("cancel") === "1") return { kind: "cancelled" };
  return { kind: "none" };
}

// ── the pathway home ──────────────────────────────────────────────────────────

/**
 * How one step row looks on the pathway home (spec screen 4):
 *
 *   done     completed: a tick, green
 *   locked   a padlock and "Unlocks after <lock.title>"
 *   current  the step Continue opens: highlighted
 *   open     any other available step
 *
 * "Optional" is not a visual state of its own: it is a flag (a dashed outline and a pill)
 * that combines with open and current.
 *
 * @param {{ state: string, current: boolean }} row  a StepRow
 * @returns {"done"|"locked"|"current"|"open"}
 */
export function stepVisual(row) {
  if (row.state === "completed") return "done";
  if (row.state === "locked") return "locked";
  if (row.current) return "current";
  return "open";
}

/** Whether a step row opens its screen: every row except a locked one. */
export const isOpenable = (row) => row.state !== "locked";

/**
 * A pathway is complete once `completed_at` is set: it is never cleared (contract §6),
 * even if a later edit adds a step.
 */
export const isPathwayComplete = (home) => Boolean(home.completed_at);

// ── the step screen ───────────────────────────────────────────────────────────

/** The share of a video that counts as watched (contract §5.4). */
export const WATCHED_SHARE = 0.9;

/**
 * Whether playback has reached the completion mark: `position / duration >= 0.9`. An
 * unknown or zero duration (metadata not loaded, a live stream) never counts.
 *
 * @param {number} position  seconds played
 * @param {number} duration  seconds in total
 */
export function watchedEnough(position, duration) {
  const p = Number(position);
  const d = Number(duration);
  if (!Number.isFinite(p) || !Number.isFinite(d) || d <= 0) return false;
  return p / d >= WATCHED_SHARE;
}

/**
 * Whether every quiz question has an answer. The body carries one choice index per
 * question, and an unanswered one is the learner's own omission to point at, not an
 * attempt to spend.
 *
 * @param {number[]} answers  -1 marks an unanswered question
 */
export const allAnswered = (answers) => answers.length > 0 && answers.every((a) => a >= 0);


// ── phase 3 step kinds (contract §10) ─────────────────────────────────────────

/** The six step kinds a learner can meet (contract §10.0.1). */
export const STEP_KINDS = Object.freeze(["video", "reading", "practice", "quiz", "live", "course"]);

/**
 * The photo types a practice submission accepts (contract §10.3): the server refuses any
 * other type with 415 PHOTO_TYPE_UNSUPPORTED.
 */
export const PHOTO_TYPES = Object.freeze(["image/jpeg", "image/png", "image/webp"]);

/**
 * The largest practice photo, in bytes (contract §10.3, "≤ 10 MB"; the server answers 413
 * PHOTO_TOO_LARGE above it). MB is 1024 × 1024 bytes, as in backend/media_routes.py.
 */
export const PHOTO_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Why a picked photo cannot be sent, checked before the upload so a learner on a phone
 * does not wait for a 12 MB transfer only to be refused.
 *
 *   { type: "image/heic", size: 2e6 }   -> "type"
 *   { type: "image/jpeg", size: 11e6 }  -> "size"
 *   { type: "image/png",  size: 3e6 }   -> null
 *
 * @param {{ type: string, size: number }} file  a File
 * @returns {"type"|"size"|null}
 */
export function photoProblem(file) {
  if (!PHOTO_TYPES.includes(file.type)) return "type";
  if (file.size > PHOTO_MAX_BYTES) return "size";
  return null;
}

/**
 * Whether a practice submission has anything to send: text that is not blank, or a photo
 * (contract §10.3: "text and photo cannot both be empty"; the server trims the text).
 *
 * @param {string} text
 * @param {File|null} photo
 */
export const hasSubmissionContent = (text, photo) => Boolean(photo) || String(text || "").trim() !== "";

/** The IANA time zone the browser runs in ("Europe/Paris"), the zone a learner reads times in. */
export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * A session instant as a learner reads it: weekday, date and time in `timeZone`, with the
 * zone's short name, so "18:00" can never be read in the wrong zone.
 *
 *   ("2026-11-12T23:00:00+00:00", "en", "America/Toronto")
 *     -> "Thursday, November 12 at 6:00 PM EST"
 *
 * @param {string} iso       ISO-8601 instant with an offset (contract §10.7 `starts_at`)
 * @param {string} lang      product language ("fr" | "en" | "es")
 * @param {string} timeZone  IANA zone
 * @returns {string} "" when absent or unreadable
 */
export function sessionTimeLabel(iso, lang, timeZone) {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleString(dateLocale(lang), {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    timeZoneName: "short",
  });
}

/**
 * The step a course was opened from (contract §10.5): the course player is linked as
 * `/learn/<course_id>?pathway=<pid>&step=<sid>`, and shows "Back to pathway" only when
 * both are present.
 *
 *   "?pathway=p1&step=s1"  -> { pathwayId: "p1", stepId: "s1" }
 *   "?pathway=p1"          -> null
 *
 * @param {string} search  `window.location.search`
 * @returns {{ pathwayId: string, stepId: string }|null}
 */
export function pathwayReturn(search) {
  const q = new URLSearchParams(search || "");
  const pathwayId = q.get("pathway");
  const stepId = q.get("step");
  return pathwayId && stepId ? { pathwayId, stepId } : null;
}
